import { handleError, logError, ErrorType, AppError } from '../utils/error/errorHandler';
import type { Database } from '@/types/supabase';
import { fetchWithAuth } from '@/lib/apiUtils';

// Define type for the poem object returned by the admin endpoint
// This might be the standard Poem type or a specific admin view
type Poem = Database['public']['Tables']['poems']['Row'] & { is_protected?: boolean };

// Define the expected input payload for the submission
interface SubmitProtectedPoemPayload {
    title: string;
    poetName: string; // Changed from poet_name to match form state
    content: string;
    subject?: string; // Optional subject
}

/**
 * Submits a new poem to be created as protected via the admin API endpoint.
 * @param payload - The poem details (title, poetName, content, subject?).
 * @returns The newly created protected poem object.
 * @throws AppError if the API call fails or returns an error.
 */
export const submitProtectedPoem = async (payload: SubmitProtectedPoemPayload): Promise<Poem> => {
    console.log('adminService: submitProtectedPoem called with payload:', payload);

    // Basic input validation
    if (!payload || !payload.title || !payload.poetName || !payload.content) {
        throw new AppError('Missing required fields (title, poetName, content) for submitting protected poem.', ErrorType.VALIDATION);
    }

    const apiUrl = '/api/admin/submit-protected-poem';

    try {
        console.log(`adminService: Calling POST ${apiUrl} with auth`);
        const response = await fetchWithAuth(apiUrl, {
            method: 'POST',
            headers: {
                // fetchWithAuth handles Authorization header
                // Content-Type is set automatically by fetchWithAuth if body is JSON string
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            let errorBody: any = null;
            try {
                errorBody = await response.json();
            } catch (parseError) { /* Ignore */ }

            console.error("adminService: submitProtectedPoem API error", {
                status: response.status,
                statusText: response.statusText,
                errorBody: errorBody,
                requestPayload: payload,
                url: apiUrl
            });

            // Throw AppError based on API response
            throw new AppError({
                message: `API Error submitting protected poem: ${errorBody?.message || response.statusText} (Status: ${response.status})`,
                type: response.status === 400 ? ErrorType.VALIDATION : ErrorType.NETWORK, // Map 400 to validation
                statusCode: response.status,
                metadata: { requestPayload: payload, responseBody: errorBody, url: apiUrl }
            });
        }

        const createdPoem: Poem = await response.json();
        console.log(`adminService: submitProtectedPoem successful, created poem ID: ${createdPoem.id}`);

        // TODO: Consider cache invalidation if admin actions should reflect immediately elsewhere
        // invalidateEntityCache(CacheEntityType.POEM);

        return createdPoem;

    } catch (error) {
        // Handle errors from fetch or thrown AppErrors
        const appError = handleError(error, 'submitProtectedPoem', { payload });
        logError(appError, 'submitProtectedPoem'); // Log the standardized error
        throw appError; // Re-throw standardized error
    }
};
